import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";

import { Message as PrimeNgMessage, ConfirmationService, MessageService } from "primeng/api";

import { Message } from "../../classes";
import { HttpStatusCodes } from "./http-status-codes";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { extractBadRequestError, extractGenericError, ForbiddenGenericError } from "./http-error-helpers";
import { UserService } from "@main/users";
import { AutoSaveDirective } from "@mipro/directives";


export interface IHttpResponsesHandler {
    handleSuccess?(): void;
    handleError?(errorResponse: HttpErrorResponse): void;
}

export interface IHttpResponseHandlerSettings {
    showSuccessMessage?: boolean; // Default false
    successMessage?: string;
    showErrorMessage?: boolean; // Default true
    errorMessage?: string;
    errorStatusesMessages?: {[key: number]: string};
    ignoreErrorMessage?: string[];
}


export class BaseHttpResponsesHandler implements IHttpResponsesHandler {
    protected isAutoSaveActive = true;
    constructor(protected injector: Injector, protected settings?: IHttpResponseHandlerSettings) { 
        this.isAutoSaveActive = this.injector.get(UserService).currentUser?.autoSaveForms;
    }


    handleSuccess(): void {
        if (this.settings && this.settings.showSuccessMessage) {
            this.showMessage(Message.Success(this.settings.successMessage ? this.settings.successMessage : "Operation succeed."));
        }
    }

    handleError(errorResponse: HttpErrorResponse): void {
        if (this.settings && this.settings.showErrorMessage == false) return;
        
        const errorMessage = extractGenericError(errorResponse);
        if (this.settings && this.settings.ignoreErrorMessage && this.settings.ignoreErrorMessage.includes(errorMessage)) return;

        if (this.settings && this.settings.errorStatusesMessages && this.settings.errorStatusesMessages[errorResponse.status] != null) {
            this.showMessage(Message.Error(this.settings.errorStatusesMessages[errorResponse.status]));
            return;
        }

        this.handleErrorHttpStatusCode(errorResponse);
    }

    protected handleErrorHttpStatusCode(errorResponse: HttpErrorResponse): void {
        // App is in Offline mode
        const appOnlineService = this.injector.get(AppOnlineStateService, null);
        const currentUser = this.injector.get(UserService).currentUser;
        if (appOnlineService && !appOnlineService.isAppOnline || currentUser.isUserRequiredSetupTwoFactor) {
            // In offline mode no response error messages are shown to user, we don't bother him
            // Or if user is currently setting up 2FA
            return;
        }

        switch (errorResponse.status) {
            case HttpStatusCodes.Status400BadRequest:
                this.handleStatus400BadRequest(errorResponse);
                break;
            case HttpStatusCodes.Status403Forbidden:
                this.handleStatus403Forbidden(errorResponse);
                break;
            case HttpStatusCodes.Status401Unauthorized:
                break;
            default:
                this.handleOtherError(errorResponse);
        }
    }

    protected handleOtherError(errorResponse: HttpErrorResponse): void {
        const errorMessage = extractGenericError(errorResponse);
        this.showMessage(Message.Error(errorMessage));
    }

    protected showMessage(message: PrimeNgMessage) {
        if (this.isAutoSaveActive && !AutoSaveDirective.showHttpHandlerMessage && AutoSaveDirective.autoSaveDirectiveInit) {
            return;
        }

        const messageService = this.injector.get(MessageService, null);
        if (messageService) {
            messageService.add(message);
        }


        AutoSaveDirective.showHttpHandlerMessage = false;
    }

    protected showMessages(messages: PrimeNgMessage[]) {
        if (this.isAutoSaveActive && !AutoSaveDirective.showHttpHandlerMessage && AutoSaveDirective.autoSaveDirectiveInit) {
            return;
        }

        const messageService = this.injector.get(MessageService, null);
        if (messageService) {
            messageService.addAll(messages);
        }
        
        AutoSaveDirective.showHttpHandlerMessage = false;
    }

    protected showDialog(message: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const confirmationService = this.injector.get(ConfirmationService, null);
            if (confirmationService) {
                confirmationService.confirm({
                    message: message,
                    accept: () => {
                        resolve(true);
                    },
                    reject: () => {
                        reject();
                    }
                });
            }
        });
    }

    private handleStatus400BadRequest(errorResponse: HttpErrorResponse): boolean {
        const errorMessage = extractBadRequestError(errorResponse);

        this.showMessage(Message.Error(errorMessage));
        return false;
    }

    private handleStatus403Forbidden(errorResponse: HttpErrorResponse): void {
        this.showMessage(Message.Error(ForbiddenGenericError));
    }
}

export class BypassResponsesHandler implements IHttpResponsesHandler {
    handleSuccess(): void { }
    handleError(errorResponse: HttpErrorResponse): void { }
}

export class OnReadByIdResponsesHandler extends BaseHttpResponsesHandler {
    constructor(private entityTitle: string, injector: Injector, settings?: IHttpResponseHandlerSettings) {
        super(injector, settings);
    }

    protected handleErrorHttpStatusCode(errorResponse: HttpErrorResponse): void {
        if (errorResponse.status == HttpStatusCodes.Status404NotFound) {
            this.showMessage(Message.Error(
                "${this.entityTitle} not found. Perhaps another user has deleted the ${this.entityTitle}. " +
                "Please consider refreshing the page."
            ));
            return;
        }

        super.handleErrorHttpStatusCode(errorResponse);
    }
}

export class OnCreateResponseHandler extends BaseHttpResponsesHandler {
    constructor(entityTitle: string, injector: Injector, settings?: IHttpResponseHandlerSettings) {
        super(injector, settings);

        if (!this.settings) this.settings = <IHttpResponseHandlerSettings> {};
        if (this.settings.showSuccessMessage == null) this.settings.showSuccessMessage = true;
        if (this.settings.successMessage == null) this.settings.successMessage = `${entityTitle} created.`;
    }
}

export class OnUpdateResponseHandler extends BaseHttpResponsesHandler {
    constructor(private entityTitle: string, injector: Injector, settings?: IHttpResponseHandlerSettings) {
        super(injector, settings);

        if (!this.settings) this.settings = <IHttpResponseHandlerSettings> {};
        if (this.settings.showSuccessMessage == null) this.settings.showSuccessMessage = true;
        if (this.settings.successMessage == null) this.settings.successMessage = `${entityTitle} updated.`;
    }


    protected handleErrorHttpStatusCode(errorResponse: HttpErrorResponse): void {
        if (errorResponse.status == HttpStatusCodes.Status404NotFound) {
            this.showMessage(Message.Error(
                `${this.entityTitle} not found. Perhaps another user has deleted the ${this.entityTitle}. ` +
                "Please consider taking a note of your changes and then refreshing the page."
            ));
            return;
        }

        super.handleErrorHttpStatusCode(errorResponse);
    }
}

export class OnDeleteResponseHandler extends BaseHttpResponsesHandler {
    constructor(private entityTitle: string, injector: Injector, settings?: IHttpResponseHandlerSettings) {
        super(injector, settings);

        if (!this.settings) this.settings = <IHttpResponseHandlerSettings> {};
        if (this.settings.showSuccessMessage == null) this.settings.showSuccessMessage = true;
        if (this.settings.successMessage == null) this.settings.successMessage = `${entityTitle} deleted.`;
    }


    protected handleErrorHttpStatusCode(errorResponse: HttpErrorResponse): void {
        if (errorResponse.status == HttpStatusCodes.Status404NotFound) {
            this.showMessage(Message.Error(
                `${this.entityTitle} not found. Perhaps another user has deleted the ${this.entityTitle}. ` +
                "Please consider taking a note of your changes and then refreshing the page."
            ));
            return;
        }

        super.handleErrorHttpStatusCode(errorResponse);
    }
}

@Injectable()
export class HttpResponsesHandlersFactory {
    constructor(private injector: Injector) {
    }

    getDefault(settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new BaseHttpResponsesHandler(this.injector, settings);
    }

    getBypass(): IHttpResponsesHandler {
        return new BypassResponsesHandler();
    }

    getForReadById(entityTitle: string, settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new OnReadByIdResponsesHandler(entityTitle, this.injector, settings);
    }

    getForReadByFilter(settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new BaseHttpResponsesHandler(this.injector, settings);
    }

    getForReadAll(settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new BaseHttpResponsesHandler(this.injector, settings);
    }

    getForCreate(entityTitle: string, settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new OnCreateResponseHandler(entityTitle, this.injector, settings);
    }

    getForUpdate(entityTitle: string, settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new OnUpdateResponseHandler(entityTitle, this.injector, settings);
    }

    getForDelete(entityTitle: string, settings?: IHttpResponseHandlerSettings): IHttpResponsesHandler {
        return new OnDeleteResponseHandler(entityTitle, this.injector, settings);
    }
}